import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, object=auth

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  };

  const forgotPassword = async (email) => {
    const { data } = await api.post("/auth/forgot-password", { email });
    return data;
  };

  const resetPassword = async (email, code, new_password) => {
    const { data } = await api.post("/auth/reset-password", { email, code, new_password });
    return data;
  };

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); }
    catch (e) { if (process.env.NODE_ENV !== "production") console.error("Logout request failed:", e); }
    setUser(false);
  }, []);

  const refreshUser = useCallback(async () => {
    try { const r = await api.get("/auth/me"); setUser(r.data); }
    catch (e) { if (process.env.NODE_ENV !== "production") console.error("Failed to refresh user:", e); }
  }, []);

  // Auto-logout after 30 minutes of inactivity (security), con aviso 5 min antes.
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    let warnTimer, logoutTimer;
    const reset = () => {
      clearTimeout(warnTimer);
      clearTimeout(logoutTimer);
      warnTimer = setTimeout(() => {
        toast.warning("Tu sesión se cerrará en 5 minutos por inactividad", { duration: 10000 });
      }, 25 * 60 * 1000);
      logoutTimer = setTimeout(async () => {
        await logout();
        toast.error("Sesión cerrada por inactividad (30 minutos)");
      }, 30 * 60 * 1000);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(warnTimer);
      clearTimeout(logoutTimer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, refreshUser, forgotPassword, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { formatApiErrorDetail };
